import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { Router } from '@angular/router';
import { debounceTime, distinctUntilChanged, filter } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { AppNotificationsService } from '../../services/app-notifications.service';
import { MatMenu } from '@angular/material/menu';
import { UtilService } from '../../services/util.service';
import { SocketService } from '@app/socket.service';
import { ApiCallService } from '@app/services/api-call.service';

@Component({
    selector: 'app-globalsearch',
    templateUrl: './globalsearch.component.html',
    styleUrls: ['./globalsearch.component.scss'],
    exportAs: 'globalSearchComponent',
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class GlobalsearchComponent implements OnInit {
  @ViewChild('searchInputModal') searchInputModal!: ElementRef;
  @ViewChild(MatMenu, { static: true }) menu!: MatMenu;

  searchInput: string = '';
  searchResults: any[] = [];
  recentSearches: any[] = [];
  isSearching: boolean = false;
  showOverlay: boolean = false;
  hoverActive: boolean = false;
  itemIndex = -1;
  itemKey = '';
  @Output() searchInputChange = new EventEmitter<number>();

  private searchSubject = new Subject<string>();
  private readonly notificationService: AppNotificationsService;

  constructor(
    service: AppNotificationsService,
    private util: UtilService,
    private socketService: SocketService,
    private notificationsService: AppNotificationsService,
    private changeDetectorRef: ChangeDetectorRef,
    private apiCallService: ApiCallService,
    private router: Router
  ) {
    this.notificationService = service;
    this.notificationService.menuEmitter.subscribe(this.toggleMenu.bind(this));

    this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      filter(term => term.length >= 3)
    ).subscribe(term => {
      this.performSearch(term);
    });
  }

  ngOnInit() {
    this.searchInput = '';
  }

  @HostListener('document:keydown.escape', ['$event'])
  onKeydownHandler(event: KeyboardEvent) {
    this.closeSearch();
  }

  // Add this new HostListener to close overlay when clicking outside
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    // Check if click is outside the search overlay
    const clickedInside = event.target instanceof Element && 
      (event.target.closest('.search-overlay') !== null || 
       event.target.closest('.search-container') !== null);
    
    if (!clickedInside && this.showOverlay) {
      this.closeSearch();
    }
  }

  onSearchInputChange() {
    const cleanedInput = this.searchInput.replace(/\s+/g, ' ').trim();
    this.searchInput = cleanedInput;
    
    const searchTerm = cleanedInput.trim();
    
    if (searchTerm.length === 0) {
      this.closeSearch();
      return;
    }
    
    if (searchTerm.length >= 3) {
      this.showOverlay = true;
      this.isSearching = true;
      this.searchSubject.next(searchTerm);
    } else {
      this.searchResults = [];
      this.isSearching = false;
      // Show overlay even for shorter terms to show recent searches
      if (searchTerm.length > 0) {
        this.showOverlay = true;
      } else {
        this.showOverlay = false;
      }
    }
  }
highlightSearchText(text: string, searchTerm: string): string {
  if (!searchTerm || !text) {
    return text;
  }
  
  const lowerText = text.toLowerCase();
  const lowerSearchTerm = searchTerm.toLowerCase();
  const startIndex = lowerText.indexOf(lowerSearchTerm);
  
  if (startIndex === -1) {
    return text;
  }
  
  const endIndex = startIndex + searchTerm.length;
  const before = text.substring(0, startIndex);
  const match = text.substring(startIndex, endIndex);
  const after = text.substring(endIndex);
  
  return `${before}<strong class="highlighted-match">${match}</strong>${after}`;
}
  performSearch(term: string) {
    const cleanedTerm = term.replace(/\s+/g, ' ').trim();
    
    this.isSearching = true;
    console.log('Searching for:', cleanedTerm);
    
    this.apiCallService.searchQuery(cleanedTerm).subscribe(
      (response) => {
        this.isSearching = false;
        if (response.body?.data) {
          this.searchResults = response.body.data;
          console.log('Search results found:', this.searchResults.length);
          this.changeDetectorRef.markForCheck();
        } else {
          this.searchResults = [];
          console.log('No search results returned');
        }
      },
      (error) => {
        this.isSearching = false;
        this.searchResults = [];
        console.error('Search error:', error);
      }
    );
  }

  selectRecentSearch(search: any) {
    const cleanedItem = search.item.replace(/\s+/g, ' ').trim();
    this.searchInput = cleanedItem;
    this.performSearch(cleanedItem);
  }

  closeSearch() {
    this.showOverlay = false;
    this.searchInput = '';
    this.searchResults = [];
    this.isSearching = false;
  }

  clearSearchInput(): void {
    this.searchInput = '';
    this.closeSearch();
  }

  onResultItemClick(search: any) {
    this.closeSearch();
    this.router.navigate(['/search', this.getRouteParams(search)]);
  }

  toggleMenu(): void {
    if (this.searchInput?.length >= 3) {
      this.searchResults = [];
      this.recentSearches = this.recentSearches.slice(0, 3);
    } else {
      this.searchResults = [];
    }
  }

  getRouteParams(search: any) {
    const moduleIdField = this.getModuleIdField(search.module);
    const routeParams: any = {
      key: search.item,
      module: search.module,
    };

    const selectedId = search?.data?.[moduleIdField];
    if (selectedId !== null && selectedId !== undefined && selectedId !== '') {
      routeParams.id = selectedId;
    }

    return routeParams;
  }

  getModuleIdField(moduleName: string): string {
    switch (moduleName) {
      case 'Leads':
        return 'lead_id';
      case 'Customer':
        return 'customer_id';
      case 'Scheme':
        return 'scheme_id';
      case 'Courses':
        return 'course_id';
      case 'Questions':
        return 'questions_id';
      case 'Assessment plan':
        return 'assessment_plan_id';
      case 'Candidate':
      case 'Candidates':
        return 'candidate_id';
      case 'Project':
        return 'project_id';
      case 'Users':
        return 'users_id';
      case 'Rates':
        return 'rates_id';
      case 'Receipt':
        return 'receipt_id';
      case 'Question paper':
        return 'question_paper_id';
      case 'Question paper version':
        return 'question_paper_version_id';
      case 'Course Version':
      case 'Course version':
        return 'course_version_id';
      case 'Business vertical':
        return 'business_vertical_id';
      case 'Industrial vertical':
        return 'industrial_vertical_id';
      case 'Question bank':
        return 'question_bank_id';
      case 'Products':
      case 'Product':
        return 'product_id';
      case 'Bills':
        return 'bills_id';
      case 'Invoices':
        return 'invoice_id';
      case 'User Roles': 
       return 'user_role_id';
      case 'User Categories':
        return 'user_category_id';
      case 'Vendors':
        return 'vendor_id';
     case 'Organization':
        return 'organization_id';
      case 'Opportunity':
        return 'opportunity_id';  
      default:
        return 'unknown_id';
    }
  } 
}
